import React, { useMemo, useState } from "react";
import { TailSpin } from "react-loader-spinner";

import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import { PROD_LINK, STAGING_LINK } from "../../constant";
import { IS_STAGING } from "../../constant/api";
import { useAdminBusinessSummaryQuery } from "../../lib/api/useAdminBusinessSummaryQuery";
import MerchantSummarySections from "./MerchantSummarySections";
import MerchantTransactionsPanel from "./MerchantTransactionsPanel";

function errorMessage(
  err: { response?: { data?: unknown } } | null | undefined,
  fallback: string
): string {
  const data = err?.response?.data;
  if (data && typeof data === "object" && "message" in data) {
    return String((data as { message?: string }).message);
  }
  return fallback;
}

const MerchantsOverview: React.FC = () => {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const dateRangeInvalid = Boolean(from && to) && from > to;

  const queryDates = useMemo(() => {
    if (from && to && !dateRangeInvalid) return { from, to };
    return {};
  }, [from, to, dateRangeInvalid]);

  const { data, isPending, isError, error, refetch, isFetching } =
    useAdminBusinessSummaryQuery({
      ...queryDates,
      enabled: !dateRangeInvalid,
    });

  const summary = data?.summary;

  return (
    <div className="w-full h-screen flex flex-col overflow-hidden">
      <div className="w-full h-[31px] shrink-0 bg-[#1B1B1B] flex justify-center items-center">
        {IS_STAGING ? (
          <h4 className="font-medium text-[#AEBCC6] text-[12px]">
            You are in Staging:{" "}
            <span>
              <a href={PROD_LINK} className="text-[#03C668]">
                Switch to Prod
              </a>
            </span>
          </h4>
        ) : (
          <h4 className="font-medium text-[#AEBCC6] text-[12px]">
            You are in PROD:{" "}
            <span>
              <a href={STAGING_LINK} className="text-[#DF4E4E]">
                Switch to Staging
              </a>
            </span>
          </h4>
        )}
      </div>
      <div className="flex flex-1 min-h-0 w-full flex-col lgss:flex-row overflow-hidden">
        <Sidebar />
        <div className="w-full lgss:w-10/12 flex-1 min-h-0 min-w-0 flex flex-col overflow-y-auto bg-[#13181B]">
          <Navbar />
          <div className="flex-1 px-5 pt-6 pb-24 mds:px-8 lgss:px-10 font-manrope bg-[#13181B] max-w-[1600px] mx-auto w-full">
            <header className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between mb-8">
              <div>
                <h1 className="font-sora font-semibold text-white text-xl tracking-tight">
                  Merchants
                </h1>
                <p className="text-[13px] text-[#869AA9] mt-1 max-w-xl leading-relaxed">
                  Business onboarding, payment pages, deposits, and payouts.
                  Leave dates empty for all-time totals.
                </p>
              </div>

              <div
                className="rounded-xl border border-[#2C363E] bg-[#161E25]/80 p-4 shadow-[0_4px_24px_rgba(0,0,0,0.25)] w-full lg:w-auto lg:min-w-[320px]"
                role="group"
                aria-label="Date range filter"
              >
                <p className="text-[11px] font-semibold text-[#869AA9] uppercase tracking-wider mb-3">
                  Date range
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                  <div className="flex-1 min-w-0">
                    <label
                      htmlFor="merchants-summary-from"
                      className="block text-[11px] text-[#5C6B76] mb-1"
                    >
                      From
                    </label>
                    <input
                      id="merchants-summary-from"
                      type="date"
                      value={from}
                      onChange={(e) => setFrom(e.target.value)}
                      className="w-full h-10 rounded-lg border border-[#2C363E] bg-[#1C2227] px-3 text-[13px] text-[#E7EBEE] outline-none focus:border-secondary_button focus:ring-1 focus:ring-secondary_button/40 transition shadow-inner"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <label
                      htmlFor="merchants-summary-to"
                      className="block text-[11px] text-[#5C6B76] mb-1"
                    >
                      To
                    </label>
                    <input
                      id="merchants-summary-to"
                      type="date"
                      value={to}
                      onChange={(e) => setTo(e.target.value)}
                      className="w-full h-10 rounded-lg border border-[#2C363E] bg-[#1C2227] px-3 text-[13px] text-[#E7EBEE] outline-none focus:border-secondary_button focus:ring-1 focus:ring-secondary_button/40 transition shadow-inner"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setFrom("");
                      setTo("");
                    }}
                    className="h-10 shrink-0 px-4 rounded-lg border border-[#2C363E] text-[12px] font-semibold text-[#AEBCC6] hover:text-white hover:bg-[#273037] hover:border-[#3A4852] transition active:scale-[0.98]"
                  >
                    Reset
                  </button>
                </div>
                {(from || to) && !(from && to) && (
                  <p className="mt-2 text-[11px] text-[#5C6B76]">
                    Select both dates to filter; otherwise all-time data is
                    shown.
                  </p>
                )}
              </div>
            </header>

            {dateRangeInvalid && (
              <div
                className="rounded-xl border border-amber-500/35 bg-amber-500/[0.07] px-5 py-8 text-center mb-6"
                role="alert"
              >
                <p className="text-[14px] font-semibold text-amber-200/95">
                  Invalid date range
                </p>
                <p className="text-[13px] text-[#AEBCC6] mt-2 max-w-md mx-auto leading-relaxed">
                  Choose an end date on or after the start date, or reset the
                  filter to view summaries.
                </p>
              </div>
            )}

            {!dateRangeInvalid && isPending && (
              <div
                className="flex flex-col items-center justify-center py-24 gap-4 rounded-xl border border-[#2C363E] bg-[#161E25]/40"
                aria-busy="true"
                aria-label="Loading merchant summary"
              >
                <TailSpin color="#0B75C2" height={40} width={40} />
                <p className="text-[13px] text-[#869AA9]">Loading summary…</p>
              </div>
            )}

            {!dateRangeInvalid && isError && (
              <div className="rounded-xl border border-red-500/35 bg-red-500/[0.08] px-4 py-4 text-[13px] text-red-100/95 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <span>
                  {errorMessage(error, "Could not load merchant summary.")}
                </span>
                <button
                  type="button"
                  onClick={() => refetch()}
                  className="shrink-0 rounded-lg bg-red-500/20 px-4 py-2 text-[12px] font-semibold text-red-200 border border-red-500/40 hover:bg-red-500/30 transition"
                >
                  Retry
                </button>
              </div>
            )}

            {!dateRangeInvalid && !isPending && !isError && summary && (
              <>
                {isFetching && !isPending && (
                  <div className="mb-4 flex items-center gap-2 text-[12px] text-[#869AA9]">
                    <span
                      className="inline-block h-3 w-3 rounded-full border-2 border-[#869AA9] border-t-transparent animate-spin"
                      aria-hidden
                    />
                    Refreshing…
                  </div>
                )}
                <MerchantSummarySections summary={summary} scope="overview" />
              </>
            )}

            {!dateRangeInvalid && (
              <MerchantTransactionsPanel
                {...queryDates}
                exportTitle="Merchants"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MerchantsOverview;
